import React, {useCallback, useEffect, useRef, useState} from "react";
import {CommentsModal} from "@webng/comments";
import FocusLock from "react-focus-lock";
import {useTranslation} from "react-i18next";
import {useLiveblogWrapperClassNames} from "../LiveblogWrapper";
import {X} from "react-feather";
import {
  closeViafouraConversation,
  currentViafouraContainerId,
  isViafouraTrayOpen,
  registerViafouraMount,
  releaseViafouraMount,
  SHOW_CONVERSATION_EVENT,
  subscribeToViafouraConversation,
  subscribeToViafouraTray
} from "./viafouraConversation";

/**
 * Where the overlay sits while Viafoura's tray is open: just below the tray's measured
 * 10,000,009, still far above typical publisher chrome. Only while the tray is open — the rest
 * of the time the shared `.tik4-modal__overlay` z-index (99,999,999) applies, unchanged. A page
 * whose chrome buries 10⁷ buries Viafoura's own login everywhere, not just here.
 */
const TRAY_STANDDOWN_OVERLAY_Z_INDEX = 10_000_000;

export interface ViafouraSidebarProps {
  /**
   * The `local_id`s of the posts that currently render a trigger, from
   * `eligibleViafouraContainerIds`. When the open conversation's post drops out of this list — filtered
   * away, paginated out, or disabled by a live update — the overlay closes itself.
   *
   * Optional: without it the overlay simply never self-closes on that ground, which is the old
   * behaviour. Callers that know their rows should pass it.
   */
  readonly eligibleContainerIds?: readonly string[];
}

export function ViafouraSidebar({eligibleContainerIds}: ViafouraSidebarProps = {}) {
  const {t} = useTranslation("liveblog");
  /*
   * The publisher's theme, taken from the same source the native comment overlay uses
   * (`chunks/Comments.tsx` passes exactly this pair as portalClassName / portalStyle).
   *
   * `className` is `tik4` plus the configured colour-scheme modifier; `style` carries the theme's
   * colours as CSS custom properties. Both are needed *here* specifically because react-modal portals
   * to document.body, outside the liveblog's wrapper — nothing is inherited (design D19).
   *
   * This replaced a hardcoded `tik4 tik4--on-light`, which silently forced light mode and dropped the
   * custom colours entirely: a dark-mode liveblog got a light Viafoura overlay. Raised by Codex on
   * PR #886.
   */
  const {className: themeClassName, style: themeStyle} = useLiveblogWrapperClassNames();
  const [containerId, setContainerId] = useState<string | undefined>(undefined);

  /*
   * Viafoura opens its login/profile UI in its notification tray — a drawer portaled to
   * document.body, i.e. OUTSIDE this overlay and its focus lock. While it is open the overlay
   * stands down (design D1 of fix-viafoura-auth-ui-occlusion): the lock is disabled so it cannot
   * fight the tray's own focus management (measured: an unbounded ~20 ms "focus-fighting" loop),
   * the overlay drops below the tray so the login view is actually visible, and a backdrop click
   * must not tear the conversation down underneath the login the reader is in the middle of.
   */
  const [trayOpen, setTrayOpen] = useState(isViafouraTrayOpen);
  useEffect(() => subscribeToViafouraTray(setTrayOpen), []);

  /*
   * Ownership, and why the subscription is not just `setContainerId`.
   *
   * The controller is module-global — one `currentContainerId`, one set of listeners, one mount node —
   * but every `Liveblog` with an eligible post mounts its OWN sidebar. On a publisher page carrying two
   * liveblog embeds that both have Viafoura posts, an open therefore reached both sidebars: each opened
   * its dialog, the one that does not own the post then found it missing from `eligibleContainerIds`
   * and called the shared close path, and the conversation shut itself the moment it was opened. Both
   * also raced to register the single mount node. Raised by Copilot on PR #886.
   *
   * So a sidebar only ever holds an id it owns. That fixes both halves at once: the non-owning sidebar
   * never opens, so it never registers a mount and its self-close effect below never sees a foreign id.
   *
   * Read through a ref rather than closed over, so the subscription is not torn down and re-made on
   * every render — and so it never reads a stale list.
   */
  const eligibleRef = useRef(eligibleContainerIds);
  eligibleRef.current = eligibleContainerIds;

  useEffect(() => subscribeToViafouraConversation(id => {
    const eligible = eligibleRef.current;
    // Without a list we cannot tell ownership, so accept everything — the single-embed behaviour, and
    // what callers that do not pass one already get.
    setContainerId(!id || !eligible || eligible.includes(id) ? id : undefined);
  }), []);

  /*
   * Close when the open conversation's post is no longer showing a trigger.
   *
   * Unmounting the overlay already covers "no eligible post at all" — its cleanup runs the shared
   * close path. This covers the narrower case that leaves the overlay mounted: the OPEN post becomes
   * ineligible while a different one stays eligible. Without it the reader keeps a conversation open
   * whose trigger has vanished, and closing it later is the only thing that tears the widget down.
   */
  useEffect(() => {
    if (!eligibleContainerIds || !containerId) {
      return;
    }
    if (!eligibleContainerIds.includes(containerId)) {
      closeViafouraConversation();
    }
  }, [eligibleContainerIds, containerId]);

  // A native comment thread opening (or another Viafoura post) dispatches this with its own id;
  // anything that is not ours means we should close.
  //
  // This must go through the shared close path rather than just dropping React state: the controller
  // keeps `currentContainerId` and `widgetActive`, and only closeViafouraConversation() tears the
  // widget down. Clearing state alone left Viafoura's realtime subscriptions open and its engagement
  // analytics unflushed.
  useEffect(() => {
    function onShow(event: Event) {
      const detail = (event as CustomEvent).detail;
      // Compare against the CONTROLLER's id, not this component's state. Opening another Viafoura
      // post dispatches this event itself, and the controller has already recorded the new id by
      // then — so an event matching it is our own and must be ignored. Comparing against React state
      // instead made switching posts *close* the overlay: the state still held the previous id, the
      // new one looked foreign, and closing cleared the id the queued open was waiting for.
      //
      // Reading the controller rather than reordering notify()/dispatch on the other side is
      // deliberate: notify() goes through React state, which is not guaranteed to have been applied
      // by the time this handler runs.
      const ours = currentViafouraContainerId();
      if (detail === ours) {
        return;
      }
      if (ours) {
        closeViafouraConversation();
      }
    }
    document.addEventListener(SHOW_CONVERSATION_EVENT, onShow);
    return () => document.removeEventListener(SHOW_CONVERSATION_EVENT, onShow);
  }, []);

  const handleClose = useCallback(() => closeViafouraConversation(), []);

  /**
   * A callback ref rather than an effect, because the node comes and goes with the modal: it fires on
   * attach and again with null on detach, which is exactly the mount lifecycle the controller needs.
   *
   * On attach, registering is what starts the pending open's work (design D21) — the open that caused
   * this overlay to appear could not do it itself, because the node did not exist yet.
   *
   * On detach, close BEFORE unregistering. Dropping the mount alone would leave `currentContainerId`
   * and `widgetActive` set, so the queued teardown would never run and Viafoura would keep
   * subscriptions and unflushed analytics alive for markup that is already gone. This happens for
   * real: the liveblog unmounts this overlay as soon as the rendered rows no longer contain a
   * renderable Viafoura post — tag filtering, pagination, or a live update disabling the post.
   */
  const ownMountRef = useRef<HTMLDivElement | null>(null);

  const setMountNode = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      ownMountRef.current = node;
      registerViafouraMount(node);
      return;
    }

    const ownNode = ownMountRef.current;
    ownMountRef.current = null;

    /*
     * Both steps are now conditional on ownership, which D24's fix did not cover — it gated the
     * subscription but left this cleanup closing the singleton unconditionally. With two embeds, opening
     * post B while A was open notified subscribers first, so A's modal started detaching, and this
     * branch then cleared B's freshly recorded conversation and notified it closed. Raised by Codex on
     * PR #886, one round after D24.
     *
     * The close still happens BEFORE the mount is released, so the queued teardown still has a node to
     * work on — that ordering is the round-3 fix and has not changed.
     */
    const current = currentViafouraContainerId();
    const eligible = eligibleRef.current;
    if (current && (!eligible || eligible.includes(current))) {
      closeViafouraConversation();
    }
    if (ownNode) {
      releaseViafouraMount(ownNode);
    }
  }, []);

  const isOpen = !!containerId;

  return <CommentsModal
    isOpen={isOpen}
    onRequestClose={handleClose}
    shouldCloseOnOverlayClick={!trayOpen}
    // Focus is entirely FocusLock's job — including returning it on close. react-modal gates BOTH
    // `markForFocusLater()` and `returnFocus()` behind this one flag (ModalPortal.js:140,118), so
    // turning it off hands over the whole responsibility rather than just the initial placement.
    // Splitting them is not on offer, and two owners would race.
    shouldFocusAfterRender={false}
    className={`${themeClassName} tik4-modal__content--viafoura-comments`}
    style={{
      content: themeStyle as any,
      ...(trayOpen ? {overlay: {zIndex: TRAY_STANDDOWN_OVERLAY_Z_INDEX}} : {})
    }}
    contentLabel={t("liveblog.viafoura.overlay.title")}
  >
    {/*
      react-modal's own Tab handling is not sufficient here, and this was measured rather than
      assumed: its `scopeTab` only intervenes when `document.activeElement` IS the first or last
      tabbable in the dialog. Viafoura re-renders its widget and destroys the focused node, at which
      point `activeElement` falls back to `<body>` — and from there `scopeTab` does nothing, so the
      browser carries focus onward, in one measured run straight onto the publisher's page.
      FocusLock re-asserts focus on focusout instead of only at the boundaries, which is exactly the
      case react-modal misses.

      It owns returning focus too, because react-modal's return is gated behind the same flag we had
      to switch off — see the prop comment above.
    */}
    {/*
      `disabled` while the tray is open; re-enabling on `tray closed` is also what brings focus
      home — react-focus-lock re-asserts into the lock when it re-engages, which is exactly the
      "containment resumes" behaviour the spec asks for, with no manual focus bookkeeping.
    */}
    <FocusLock disabled={trayOpen} returnFocus={true} autoFocus={true} className="tik4-comment-modal tik4-vf__lock">
      <div className="tik4-modal__header">
        <div className="tik4-modal__header-title"></div>
        <button className="tik4-modal__close" onClick={handleClose} type="button"><X className="tik4-icon tik4-modal__close__icon" /></button>
      </div>

      {/*
        Viafoura owns everything below. It replaces the <vf-conversations> element the controller writes
        here with its own widget, so React must never render children into this node.

        `viafoura` is load-bearing, not decoration: 98% of the selectors in Viafoura's own stylesheets
        are scoped under `.viafoura` (e.g. `.viafoura .vf-button{…}`), and their bundle wraps widgets it
        injects itself in `<div class="viafoura">`. Without an ancestor carrying it, all six of their
        stylesheets load and match nothing, and the conversation renders completely unstyled — raw
        browser buttons in Arial. Kept on this node rather than on body/html so their CSS is scoped to
        their widget instead of leaking across the publisher's page.
      */}
      <div className="tik4-comment-modal__body">
        <div className="viafoura" ref={setMountNode} />
      </div>
    </FocusLock>
  </CommentsModal>;
}

export default ViafouraSidebar;
